import type { ComponentProps, ReactNode } from 'react';
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import { useRefWrappedCallback } from '@elfsight/lib-utils';
import type { Swiper, SwiperProps } from '@elfsight/reexported-swiper';
import {
  Autoplay,
  EffectCards,
  EffectCoverflow,
  EffectCreative,
  EffectCube,
  EffectFade,
  EffectFlip,
  ReactSwiper,
  ReactSwiperSlide,
  swiperEffectCardsCSS,
  swiperEffectCoverflowCSS,
  swiperEffectCreativeCSS,
  swiperEffectCubeCSS,
  swiperEffectFadeCSS,
  swiperEffectFlipCSS,
  swiperSwiperCSS
} from '@elfsight/reexported-swiper';
import {
  createReadableClass,
  useRerender,
  useStableRect
} from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import { CarouselAPIContext } from './CarouselAPIContext';
import { CarouselItemStateContext } from './CarouselItemStateContext';
import { ArrowControl, BulletsControl } from './components';
import { carouselEffectsSettings } from './constants';
import type { CarouselAPI } from './types';
import { useArrowOffsets } from './utils';

type ControlPosition = 'inside' | 'outside' | 'adaptive';

function isSwiperAlive(swiper: Swiper | undefined): swiper is Swiper {
  return !!swiper && !swiper.destroyed;
}

export type CarouselProps = Omit<
  ComponentProps<typeof CarouselContainer>,
  'children'
> &
  SwiperProps & {
    children?: ReactNode;
    ariaLabel?: string;
    initialItemIndex?: number;
    itemsPerSlide?: number;
    itemWidth?: number;
    gap?: number;
    centered?: boolean;
    slideBy?: 'item' | 'slide';
    withArrowControl?: boolean;
    showArrowsControlOnHover?: boolean;
    arrowControlPosition?: ControlPosition;
    arrowControlSize?: number;
    arrowControlMargin?: number;
    arrowControlVariant?: 'normal' | 'ghost';
    arrowControlBackgroundColor?: string;
    arrowControlBackgroundColorOnHover?: string;
    arrowControlIconVariant?: 'angle' | 'arrow';
    arrowControlIconColor?: string;
    arrowControlIconColorOnHover?: string;
    withPaginationControl?: boolean;
    paginationControlBackgroundColor?: string;
    paginationControlPosition?: 'inside' | 'outside';
    paginationForceStaticBigBullet?: boolean;
    paginationBulletSize?: number;
    paginationBulletGap?: number;
    paginationBackgroundColor?: string;
    paginationBackgroundPadding?: number;
    paginationBackgroundCornerRadius?: number;
    bulletInsideGap?: number;
    withAutoplay?: boolean;
    autoplayDelay?: number;
    withoutTransition?: boolean;
    rtl?: boolean;
    animationSpeed?: number;
    pauseOnHover?: boolean;
    reportSlideChangeInstantly?: boolean;
    onReachEnd?: () => void;
    onVisibleItemsChange?: (visibleItemsIndexes: number[]) => void;
    onCurrentItemChange?: (currentItemIndex: number) => void;
    onSlideToPrev?: (index: number) => void;
    onSlideToNext?: (index: number) => void;
    containerHorizontalPadding?: number;
  };

export const Carousel = forwardRef<CarouselAPI, CarouselProps>(
  function Carousel(
    {
      children,
      ariaLabel = 'Carousel',
      initialItemIndex = 0,
      height,
      itemsPerSlide,
      itemWidth,
      gap = 0,
      threshold = 20,
      centered = false,
      loop = false,
      slideBy = 'item',
      withArrowControl = false,
      showArrowsControlOnHover = false,
      arrowControlPosition = 'inside',
      arrowControlSize = 32,
      arrowControlMargin = 8,
      arrowControlVariant = 'normal',
      arrowControlBackgroundColor = 'rgba(17, 17, 17, 0.5)',
      arrowControlBackgroundColorOnHover,
      arrowControlIconVariant = 'angle',
      arrowControlIconColor,
      arrowControlIconColorOnHover,
      withAutoplay = false,
      withPaginationControl = true,
      paginationControlBackgroundColor = 'rgb(17, 17, 17)',
      paginationControlPosition = 'outside',
      paginationForceStaticBigBullet,
      paginationBulletSize,
      paginationBulletGap,
      paginationBackgroundColor,
      paginationBackgroundPadding,
      paginationBackgroundCornerRadius,
      bulletInsideGap,
      creativeEffect,
      autoplayDelay = 5000,
      withoutTransition = false,
      rtl = false,
      effect = 'slide',
      pauseOnHover = true,
      animationSpeed = 300,
      reportSlideChangeInstantly = false,
      allowTouchMove = true,
      touchStartPreventDefault = true,
      onInit,
      onReachEnd,
      onSlideChange,
      onVisibleItemsChange,
      onCurrentItemChange,
      onSlideToPrev,
      onSlideToNext,
      containerHorizontalPadding,
      ...forwardingProps
    },
    forwardingAPIRef
  ) {
    // filter invalid elements
    const items = Children.toArray(children).filter(isValidElement);

    const leftArrowDirection = rtl ? 'right' : 'left';
    const rightArrowDirection = rtl ? 'left' : 'right';

    const effectSettings = carouselEffectsSettings[effect] || {};
    const InnerWithEffectStyles = EffectLayouts[effect];

    const swiperRef = useRef<Swiper>();
    const setSwiperRef = (swiperInstance: Swiper) => {
      swiperRef.current = swiperInstance;
      onInit?.(swiperInstance);
    };

    const [currentItemIndex, setCurrentItemIndex] = useState(initialItemIndex);
    const [visibleItemsIndexes, setVisibleItemIndexes] = useState<number[]>([]);
    const [[isBeginning, isEnd], setSwiperBindings] = useState<
      [boolean, boolean]
    >([false, false]);

    const slideSpeed = useMemo(
      () => (withoutTransition ? 0 : animationSpeed),
      [withoutTransition, animationSpeed]
    );

    // create callback to update Swiper state
    const handleSwiperStateUpdate = useRefWrappedCallback(
      (swiperInstance: Swiper) => {
        // check whether the bindings have changed
        if (
          swiperInstance.isBeginning !== isBeginning ||
          swiperInstance.isEnd !== isEnd
        ) {
          setSwiperBindings([swiperInstance.isBeginning, swiperInstance.isEnd]);
        }

        // set the current item index
        setCurrentItemIndex(swiperInstance.realIndex);

        // optional crutch, bcz slideChangeTransitionEnd doesn't always fire on swipe, probably bug in swiper
        if (reportSlideChangeInstantly) {
          onSlideChange?.(swiperInstance);
        }

        const { loopedSlides, visibleSlidesIndexes } =
          swiperInstance as unknown as {
            loopedSlides: number;
            visibleSlidesIndexes: number[];
          };

        if (!visibleSlidesIndexes || swiperInstance.animating) {
          return;
        }

        let nextVisibleItemsIndexes: number[];

        if (loop) {
          // get visible items indexes considering
          // the duplicated (by the loop) items
          nextVisibleItemsIndexes = visibleSlidesIndexes.map(
            (index) =>
              (index < loopedSlides
                ? swiperInstance.slides.length - index + 1
                : index - loopedSlides) % items.length
          );
        } else {
          nextVisibleItemsIndexes = visibleSlidesIndexes;
        }

        // check whether the visible items have changed
        if (
          nextVisibleItemsIndexes.toString() !== visibleItemsIndexes.toString()
        ) {
          setVisibleItemIndexes(nextVisibleItemsIndexes);
        }
      }
    );

    const hasPrevious = loop || !isBeginning;
    const hasNext = loop || !isEnd;

    const rerenderHook = useRerender();
    const rerender = useCallback(() => {
      if (swiperRef.current) {
        swiperRef.current.update();
      }
      rerenderHook();
    }, [rerenderHook]);

    const slideTo = useCallback((itemIndex: number, speed?: number) => {
      if (swiperRef.current && !swiperRef.current.destroyed) {
        swiperRef.current.slideToLoop(itemIndex, speed);
      }
    }, []);

    const slideToPrevious = useCallback(() => {
      if (swiperRef.current) {
        swiperRef.current.slidePrev();

        onSlideToPrev?.(swiperRef.current.realIndex);
      }
    }, [onSlideToPrev]);

    const slideToNext = useCallback(() => {
      if (swiperRef.current) {
        swiperRef.current.slideNext(slideSpeed);

        onSlideToNext?.(swiperRef.current.realIndex);
      }
    }, [slideSpeed, onSlideToNext]);

    const autoplayIsRunning: () => boolean = useCallback(() => {
      return swiperRef?.current?.autoplay.running || false;
    }, []);

    const autoplayStart = useCallback(() => {
      if (swiperRef?.current?.autoplay) {
        swiperRef.current.autoplay.start();
      }
    }, []);

    const autoplayStop = useCallback(() => {
      if (swiperRef?.current?.autoplay) {
        swiperRef.current.autoplay.stop();
      }
    }, []);

    const updateAutoHeight = useCallback(() => {
      if (swiperRef?.current?.params) {
        swiperRef.current.updateAutoHeight();
      }
    }, []);

    const carouselAPI = useMemo(
      () => ({
        currentItemIndex,
        visibleItemsIndexes,
        hasPrevious,
        hasNext,
        slideTo,
        slideToPrevious,
        slideToNext,
        rerender,
        updateAutoHeight,
        autoplayStart,
        autoplayStop,
        autoplayIsRunning
      }),
      [
        currentItemIndex,
        visibleItemsIndexes,
        hasPrevious,
        hasNext,
        slideTo,
        slideToPrevious,
        slideToNext,
        rerender,
        updateAutoHeight,
        autoplayStart,
        autoplayStop,
        autoplayIsRunning
      ]
    );

    useImperativeHandle(forwardingAPIRef, () => carouselAPI, [carouselAPI]);

    // handle current item change callback
    const onCurrentItemChangeRef = useRef(onCurrentItemChange);

    useEffect(() => {
      if (!swiperRef.current) {
        return;
      }

      const autoplaySettings = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        reverseDirection: false,
        stopOnLastSlide: false,
        waitForTransition: true
      };

      swiperRef.current.params.autoplay =
        withAutoplay && autoplayDelay > 0 ? autoplaySettings : false;
    }, [withAutoplay, autoplayDelay]);

    useEffect(() => {
      onCurrentItemChangeRef.current = onCurrentItemChange;
    }, [onCurrentItemChange]);

    useEffect(() => {
      if (onCurrentItemChangeRef.current) {
        onCurrentItemChangeRef.current(currentItemIndex);
      }
    }, [currentItemIndex]);

    // handle visible items change callback
    const onVisibleItemsChangeRef = useRef(onVisibleItemsChange);

    useEffect(() => {
      onVisibleItemsChangeRef.current = onVisibleItemsChange;
    }, [onVisibleItemsChange]);

    useEffect(() => {
      if (onVisibleItemsChangeRef.current) {
        onVisibleItemsChangeRef.current(visibleItemsIndexes);
      }
    }, [visibleItemsIndexes]);

    // handle reach end callback
    const onReachEndRef = useRef(onReachEnd);

    useEffect(() => {
      onReachEndRef.current = onReachEnd;
    }, [onReachEnd]);

    useEffect(() => {
      if (onReachEndRef.current && !hasNext) {
        onReachEndRef.current();
      }
    }, [hasNext]);

    const [swiperWrapperWidth, setSwiperWrapperWidth] = useState(0);
    const [swiperWrapperRectRef] = useStableRect(
      { width: swiperWrapperWidth },
      ({ width }) => setSwiperWrapperWidth(width)
    );

    useEffect(() => {
      if (swiperRef.current) {
        swiperRef.current.update();
      }
    }, [swiperWrapperWidth]);

    useEffect(() => {
      if (swiperRef.current) {
        handleSwiperStateUpdate(swiperRef.current);
      }
    }, [handleSwiperStateUpdate, children]);

    // determine arrow control properties
    const arrowControlVariantGhost = arrowControlVariant === 'ghost';
    const arrowControlPositionInside = arrowControlPosition === 'inside';
    const arrowControlPositionOutside = arrowControlPosition === 'outside';
    const arrowControlPositionAdaptive = arrowControlPosition === 'adaptive';

    const { leftArrowOffset, rightArrowOffset, ref } = useArrowOffsets({
      arrowControlSize,
      horizontalPadding: containerHorizontalPadding,
      enabled: withArrowControl && arrowControlPositionAdaptive
    });

    // fix the loop jump issue
    const loopJumpFixRef = useRef<[boolean, number[]]>([
      loop,
      visibleItemsIndexes
    ]);

    useEffect(() => {
      const [previousLoop, previousVisibleItemsIndexes] =
        loopJumpFixRef.current;
      const swiper = swiperRef.current;
      const shouldSkipLoopJumpFix = (isEnd && !loop) || !isSwiperAlive(swiper);

      if (shouldSkipLoopJumpFix) {
        return;
      }

      const lastVisibleIndex =
        previousVisibleItemsIndexes[previousVisibleItemsIndexes.length - 1];
      const firstVisibleIndex = previousVisibleItemsIndexes[0];

      let targetSlideIndex =
        itemsPerSlide === 1
          ? (lastVisibleIndex ?? firstVisibleIndex)
          : firstVisibleIndex;

      if (loop === previousLoop) {
        targetSlideIndex = currentItemIndex;
      }

      const animationFrameId = requestAnimationFrame(() => {
        if (!isSwiperAlive(swiper)) {
          return;
        }

        swiper.slideToLoop(targetSlideIndex, 0);
      });

      return () => cancelAnimationFrame(animationFrameId);
    }, [loop, itemsPerSlide]);

    useEffect(() => {
      loopJumpFixRef.current = [loop, visibleItemsIndexes];
    }, [loop, visibleItemsIndexes]);

    useEffect(() => {
      withAutoplay ? autoplayStart() : autoplayStop();
    }, [withAutoplay, autoplayStart, autoplayStop, effect, itemsPerSlide, rtl]);

    // clear slides styles, after change direction
    useEffect(() => {
      if (swiperRef.current && !swiperRef.current.destroyed) {
        const slides = swiperRef.current.slides;
        slides.forEach((element: Element) => {
          const styles = element
            ?.getAttribute('style')
            ?.replace(`margin-right: ${gap}px;`, '')
            ?.replace(`margin-left: ${gap}px;`, '');
          element.setAttribute('style', styles || '');
        });

        swiperRef.current.changeLanguageDirection(rtl ? 'rtl' : 'ltr');
        swiperRef.current.slideToLoop(initialItemIndex, 700);
      }
    }, [rtl]);

    useEffect(() => {
      if (swiperRef.current) {
        swiperRef.current.allowTouchMove = allowTouchMove;
      }
    }, [allowTouchMove]);

    return (
      <CarouselContainer
        {...forwardingProps}
        ref={ref}
        style={{
          gap:
            withPaginationControl &&
            paginationControlPosition === 'outside' &&
            items?.length
              ? clamp(gap, 12, 40)
              : undefined
        }}
        onMouseEnter={withAutoplay && pauseOnHover ? autoplayStop : undefined}
        onMouseLeave={withAutoplay && pauseOnHover ? autoplayStart : undefined}
        _showArrowsControlOnHover={showArrowsControlOnHover}
      >
        <CarouselOuter>
          {withArrowControl && (
            <CarouselArrowControlContainer
              aria-label="Previous"
              aria-hidden={!hasPrevious}
              role="button"
              style={{
                width: arrowControlSize,
                height: arrowControlSize,
                flexBasis: arrowControlSize,
                marginLeft: arrowControlPositionInside
                  ? arrowControlMargin
                  : undefined,
                marginRight: arrowControlPositionOutside
                  ? arrowControlMargin
                  : undefined,
                [leftArrowDirection]: -leftArrowOffset
              }}
              _floating={!arrowControlPositionOutside}
            >
              {hasPrevious && (
                <ArrowControl
                  direction={leftArrowDirection}
                  ghost={arrowControlVariantGhost}
                  size={arrowControlSize}
                  backgroundColor={arrowControlBackgroundColor}
                  backgroundColorOnHover={arrowControlBackgroundColorOnHover}
                  iconVariant={arrowControlIconVariant}
                  iconColor={arrowControlIconColor}
                  iconColorOnHover={arrowControlIconColorOnHover}
                  onClick={slideToPrevious}
                />
              )}
            </CarouselArrowControlContainer>
          )}
          <InnerWithEffectStyles aria-label={ariaLabel} role="region">
            <CarouselSwiperWrapper ref={swiperWrapperRectRef}>
              <CarouselAPIContext.Provider value={carouselAPI}>
                <ReactSwiper
                  touchStartPreventDefault={touchStartPreventDefault}
                  initialSlide={initialItemIndex}
                  height={height}
                  autoHeight={height === undefined}
                  slidesPerView={itemsPerSlide ?? 'auto'}
                  spaceBetween={gap}
                  loop={loop}
                  loopedSlides={
                    loop && centered ? Math.ceil(items.length / 2) : undefined
                  }
                  slidesPerGroup={
                    !centered && slideBy === 'slide' ? itemsPerSlide : 1
                  }
                  slideToClickedSlide={centered}
                  threshold={threshold}
                  speed={slideSpeed}
                  onSwiper={setSwiperRef}
                  onInit={handleSwiperStateUpdate}
                  onSlideChange={handleSwiperStateUpdate}
                  onTransitionEnd={handleSwiperStateUpdate}
                  onSlideChangeTransitionEnd={(swiper: Swiper) => {
                    if (onSlideChange) {
                      onSlideChange(swiper);
                    }
                  }}
                  onLoopFix={handleSwiperStateUpdate}
                  dir={rtl ? 'rtl' : 'ltr'}
                  centeredSlides={centered}
                  effect={effect}
                  // Works only with effect === 'creative'
                  creativeEffect={creativeEffect}
                  modules={[
                    EffectCoverflow,
                    EffectCube,
                    EffectFlip,
                    EffectCards,
                    EffectFade,
                    EffectCreative,
                    Autoplay
                  ]}
                  watchSlidesProgress
                  {...effectSettings}
                >
                  {items.map((item, index) => (
                    <ReactSwiperSlide
                      key={index}
                      style={itemWidth ? { width: itemWidth } : undefined}
                    >
                      {({
                        isVisible,
                        isActive,
                        isPrev,
                        isNext
                      }: {
                        isVisible: boolean;
                        isActive: boolean;
                        isPrev: boolean;
                        isNext: boolean;
                      }) => (
                        <CarouselItemStateContext.Provider
                          value={{
                            isVisible,
                            isCurrent: isActive,
                            isPrevious: isPrev,
                            isNext
                          }}
                        >
                          {item}
                        </CarouselItemStateContext.Provider>
                      )}
                    </ReactSwiperSlide>
                  ))}
                </ReactSwiper>
              </CarouselAPIContext.Provider>
            </CarouselSwiperWrapper>
          </InnerWithEffectStyles>

          {withArrowControl && (
            <CarouselArrowControlContainer
              aria-label="Next"
              aria-hidden={!hasNext}
              role="button"
              style={{
                width: arrowControlSize,
                height: arrowControlSize,
                flexBasis: arrowControlSize,
                marginLeft: arrowControlPositionOutside
                  ? arrowControlMargin
                  : undefined,
                marginRight: arrowControlPositionInside
                  ? arrowControlMargin
                  : undefined,
                [rightArrowDirection]: -rightArrowOffset
              }}
              _floating={!arrowControlPositionOutside}
            >
              {hasNext && (
                <ArrowControl
                  direction={rightArrowDirection}
                  ghost={arrowControlVariantGhost}
                  size={arrowControlSize}
                  backgroundColor={arrowControlBackgroundColor}
                  backgroundColorOnHover={arrowControlBackgroundColorOnHover}
                  iconVariant={arrowControlIconVariant}
                  iconColor={arrowControlIconColor}
                  iconColorOnHover={arrowControlIconColorOnHover}
                  onClick={slideToNext}
                />
              )}
            </CarouselArrowControlContainer>
          )}
        </CarouselOuter>
        {withPaginationControl && (
          <BulletsControl
            bulletSize={paginationBulletSize}
            bulletGap={paginationBulletGap}
            backgroundColor={paginationBackgroundColor}
            backgroundPadding={paginationBackgroundPadding}
            backgroundCornerRadius={paginationBackgroundCornerRadius}
            forceStaticBigBullet={paginationForceStaticBigBullet}
            currentItemIndex={currentItemIndex}
            items={items}
            loop={loop}
            itemsPerSlide={visibleItemsIndexes.length || 1}
            slideBy={slideBy}
            position={paginationControlPosition}
            color={paginationControlBackgroundColor}
            insideGap={bulletInsideGap}
            onSlideTo={slideTo}
            rtl={rtl}
          />
        )}
      </CarouselContainer>
    );
  }
);

export default Carousel;

export const CarouselContainer = styled.div.attrs<{
  _showArrowsControlOnHover?: boolean;
}>({
  className: createReadableClass({
    component: 'carousel',
    element: 'container'
  })
})`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;

  ${({ _showArrowsControlOnHover }) =>
    _showArrowsControlOnHover &&
    css`
      ${CarouselArrowControlContainer} {
        display: none;
      }

      &:hover ${CarouselArrowControlContainer} {
        display: block;
      }
    `}
`;

export const CarouselOuter = styled.div.attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'outer'
  })
})`
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
`;

export const CarouselInner = styled.div.attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner'
  })
})`
  ${swiperSwiperCSS};
  position: relative;
  z-index: 1;
  overflow: hidden;
  flex: 1;
  max-width: 100%;

  .swiper-slide {
    align-self: initial;
  }
`;

const CarouselInnerWithCoverflow = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'coverflow'
  })
})`
  ${swiperEffectCoverflowCSS}
  .swiper-slide {
    transition:
      opacity 0.5s ease,
      transform 0.5s ease;
    opacity: 0;
  }

  .swiper-slide-visible {
    opacity: 1;
  }
`;

const CarouselInnerWithCards = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'cards'
  })
})`
  ${swiperEffectCardsCSS}
`;

const CarouselInnerWithCube = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'cube'
  })
})`
  ${swiperEffectCubeCSS}
`;

const CarouselInnerWithFlip = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'flip'
  })
})`
  ${swiperEffectFlipCSS}
`;

const CarouselInnerWithFade = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'fade'
  })
})`
  ${swiperEffectFadeCSS}
`;

const CarouselInnerWithCreative = styled(CarouselInner).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'inner',
    modifier: 'creative'
  })
})`
  ${swiperEffectCreativeCSS}
`;

const EffectLayouts = {
  cards: CarouselInnerWithCards,
  coverflow: CarouselInnerWithCoverflow,
  slide: CarouselInner,
  cube: CarouselInnerWithCube,
  flip: CarouselInnerWithFlip,
  fade: CarouselInnerWithFade,
  creative: CarouselInnerWithCreative,
  undefined: CarouselInner
};

export interface CarouselArrowControlContainerProps {
  _floating: boolean;
}

export const CarouselArrowControlContainer = styled.div.attrs<CarouselArrowControlContainerProps>(
  {
    className: createReadableClass({
      component: 'carousel',
      element: 'arrow-control-container'
    })
  }
)`
  position: relative;
  z-index: 2;
  flex: 0;

  ${({ _floating }) =>
    _floating &&
    css`
      position: absolute;
      top: 50%;
      transform: translate(0, -50%);
    `}
`;

export const CarouselNavigationControlContainer = styled.div.attrs<{
  _floating: boolean;
}>({
  className: createReadableClass({
    component: 'carousel',
    element: 'navigation-control-container'
  })
})`
  position: relative;
  z-index: 2;

  ${({ _floating }) =>
    _floating &&
    css`
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, 0);
    `}
`;

export const CarouselSwiperWrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'swiper-wrapper'
  })
})`
  width: 100%;

  .swiper {
    max-width: 100%;
  }
`;
